/**
 * Kestovar Performance Monitor — Web Vitals tracking
 * Reports Core Web Vitals (LCP, FID, CLS, INP) for production monitoring.
 */

import { useEffect, useRef } from 'react';
import { logger } from '@/lib/logger';

export interface WebVitalsReport {
  lcp?: number; // Largest Contentful Paint (ms)
  fid?: number; // First Input Delay (ms)
  cls?: number; // Cumulative Layout Shift
  inp?: number; // Interaction to Next Paint (ms)
  fcp?: number; // First Contentful Paint (ms)
  ttfb?: number; // Time to First Byte (ms)
}

const reportedVitals: Partial<WebVitalsReport> = {};

/** Get current vitals snapshot */
export function getVitals(): Partial<WebVitalsReport> {
  return { ...reportedVitals };
}

/** Get performance score 0-100 based on vitals */
export function getPerformanceScore(): number {
  const v = reportedVitals;
  let score = 100;
  
  // LCP: <2.5s good, <4s needs improvement, >4s poor
  if (v.lcp) {
    if (v.lcp > 4000) score -= 25;
    else if (v.lcp > 2500) score -= 10;
  }
  
  // CLS: <0.1 good, <0.25 needs improvement, >0.25 poor
  if (v.cls) {
    if (v.cls > 0.25) score -= 25;
    else if (v.cls > 0.1) score -= 10;
  }
  
  // FID: <100ms good, <300ms needs improvement, >300ms poor
  if (v.fid) {
    if (v.fid > 300) score -= 20;
    else if (v.fid > 100) score -= 8;
  }
  
  // INP: <200ms good, <500ms needs improvement, >500ms poor
  if (v.inp) {
    if (v.inp > 500) score -= 20;
    else if (v.inp > 200) score -= 8;
  }
  
  return Math.max(0, score);
}

/** Hook to track and report Web Vitals */
export function useWebVitals() {
  const observerRef = useRef<PerformanceObserver | null>(null);

  useEffect(() => {
    // Largest Contentful Paint
    if ('PerformanceObserver' in window) {
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          if (lastEntry) {
            reportedVitals.lcp = Math.round(lastEntry.startTime);
            logger.log('Performance', `LCP: ${reportedVitals.lcp}ms`);
          }
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] as any });
        observerRef.current = lcpObserver;
      } catch {
        // LCP not supported
      }

      // First Input Delay
      try {
        const fidObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const fidEntry = entry as PerformanceEventTiming;
            if (fidEntry.processingStart && fidEntry.startTime) {
              reportedVitals.fid = Math.round(fidEntry.processingStart - fidEntry.startTime);
              logger.log('Performance', `FID: ${reportedVitals.fid}ms`);
            }
          }
        });
        fidObserver.observe({ entryTypes: ['first-input'] as any });
      } catch {
        // FID not supported
      }

      // Cumulative Layout Shift
      try {
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const layoutEntry = entry as any;
            if (!layoutEntry.hadRecentInput) {
              clsValue += layoutEntry.value;
            }
          }
          reportedVitals.cls = Math.round(clsValue * 1000) / 1000;
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] as any });
      } catch {
        // CLS not supported
      }
    }

    // First Contentful Paint (from Navigation Timing)
    const reportFCP = () => {
      const paintEntries = performance.getEntriesByType('paint');
      const fcp = paintEntries.find(e => e.name === 'first-contentful-paint');
      if (fcp) {
        reportedVitals.fcp = Math.round(fcp.startTime);
      }
      const ttfb = performance.timing?.responseStart - performance.timing?.navigationStart;
      if (ttfb > 0) {
        reportedVitals.ttfb = ttfb;
      }
    };

    // Report after page load
    if (document.readyState === 'complete') {
      reportFCP();
    } else {
      window.addEventListener('load', reportFCP);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);
}

/** Track custom performance marks */
export function markPerformance(label: string) {
  if (performance.mark) {
    performance.mark(`plp-${label}`);
    logger.log('Performance', `Mark: ${label}`);
  }
}

/** Measure between two marks */
export function measurePerformance(name: string, startLabel: string, endLabel?: string) {
  try {
    performance.measure(`plp-${name}`, `plp-${startLabel}`, endLabel ? `plp-${endLabel}` : undefined);
    const entries = performance.getEntriesByName(`plp-${name}`);
    const last = entries[entries.length - 1];
    if (last) {
      logger.log('Performance', `${name}: ${Math.round(last.duration)}ms`);
      return Math.round(last.duration);
    }
  } catch {
    // Measurement failed
  }
  return null;
}
